import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import { createYoutubeChannel, createYoutubeUpload, deleteYoutubeChannel, enqueueYoutubeUpload, getAiSettings, getImageSettings, listMediaAssets, listVideos, listVoiceProfiles, listYoutubeChannels, listYoutubeUploads, mintPublicAssetLink, retryYoutubeUpload, startYoutubeOauth, updateYoutubeChannel, uploadMediaAsset } from '../../services/contentAiService';

function extractGoogleOAuthClient(json) {
  const root = json && typeof json === 'object' ? json : null;
  if (!root) return null;
  const candidate = (root.web && typeof root.web === 'object')
    ? root.web
    : ((root.installed && typeof root.installed === 'object') ? root.installed : root);
  const clientId = candidate?.client_id ? String(candidate.client_id).trim() : '';
  const clientSecret = candidate?.client_secret ? String(candidate.client_secret).trim() : '';
  const projectId = candidate?.project_id ? String(candidate.project_id).trim() : (root?.web?.project_id ? String(root.web.project_id).trim() : '');
  if (!clientId || !clientSecret) return null;
  return { clientId, clientSecret, projectId: projectId || null };
}

function parsePostingTimesText(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  const out = [];
  const seen = new Set();
  for (const part of raw.split(/[\n,;]+/g)) {
    const s = String(part || '').trim();
    if (!s) continue;
    if (!/^\d{2}:\d{2}$/.test(s)) return null;
    const [hourRaw, minuteRaw] = s.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

export default function YouTubePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [videos, setVideos] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [aiSettings, setAiSettings] = useState(null);
  const [imageSettings, setImageSettings] = useState(null);

  const [openChannel, setOpenChannel] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState(null);
  const [channelName, setChannelName] = useState('');
  const [channelLanguage, setChannelLanguage] = useState('');
  const [channelNiche, setChannelNiche] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [defaultVoiceProfileId, setDefaultVoiceProfileId] = useState('');
  const [defaultAiModel, setDefaultAiModel] = useState('');
  const [defaultImageModel, setDefaultImageModel] = useState('');
  const [postingFrequency, setPostingFrequency] = useState('');
  const [postingTimesText, setPostingTimesText] = useState('');
  const [monthlyBudgetUsd, setMonthlyBudgetUsd] = useState('');
  const [growthGoal, setGrowthGoal] = useState('');
  const [operationalStatus, setOperationalStatus] = useState('ACTIVE');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [openDeleteChannel, setOpenDeleteChannel] = useState(false);
  const [deletingChannel, setDeletingChannel] = useState(null);

  const [openUpload, setOpenUpload] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [privacyStatus, setPrivacyStatus] = useState('private');
  const [categoryId, setCategoryId] = useState('27');
  const [playlistId, setPlaylistId] = useState('');
  const [isShorts, setIsShorts] = useState(false);
  const [seoAuto, setSeoAuto] = useState(true);
  const [tagsText, setTagsText] = useState('');
  const [hashtagsText, setHashtagsText] = useState('');
  const [titleOverride, setTitleOverride] = useState('');
  const [descriptionOverride, setDescriptionOverride] = useState('');
  const [madeForKids, setMadeForKids] = useState(false);

  const [openMediaBank, setOpenMediaBank] = useState(false);
  const [mediaBankChannel, setMediaBankChannel] = useState(null);
  const [mediaBankLoading, setMediaBankLoading] = useState(false);
  const [mediaBankUploadLoading, setMediaBankUploadLoading] = useState(false);
  const [mediaBankImages, setMediaBankImages] = useState([]);
  const [mediaBankVideos, setMediaBankVideos] = useState([]);
  const [mediaBankPreviewUrls, setMediaBankPreviewUrls] = useState({});

  const readyVideos = useMemo(() => (videos || []).filter(v => String(v.status || '') === 'ready'), [videos]);

  const aiModelOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    const push = (value, label) => {
      const v = String(value || '').trim();
      if (!v || seen.has(v)) return;
      seen.add(v);
      opts.push({ value: v, label });
    };
    const openaiModel = String(aiSettings?.openaiModel || 'gpt-4o-mini').trim();
    const anthropicModel = String(aiSettings?.anthropicModel || 'claude-3-5-sonnet-latest').trim();
    const geminiModel = String(aiSettings?.geminiModel || 'gemini-2.5-flash').trim();
    const deepseekModel = String(aiSettings?.deepseekModel || 'deepseek-chat').trim();

    push(openaiModel, `${openaiModel} (OpenAI)`);
    push(anthropicModel, `${anthropicModel} (Claude)`);
    push(geminiModel, `${geminiModel} (Gemini)`);
    push(deepseekModel, `${deepseekModel} (DeepSeek)`);
    push(defaultAiModel, `${defaultAiModel} (atual)`);
    return opts;
  }, [aiSettings, defaultAiModel]);

  const imageModelOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    const push = (value, label) => {
      const v = String(value || '').trim();
      if (!v || seen.has(v)) return;
      seen.add(v);
      opts.push({ value: v, label });
    };
    push(imageSettings?.openaiImageModel, `${imageSettings?.openaiImageModel || ''} (OpenAI)`);
    push(defaultImageModel, `${defaultImageModel} (atual)`);
    return opts;
  }, [imageSettings, defaultImageModel]);

  const resetChannelForm = () => {
    setOpenChannel(false);
    setEditingChannelId(null);
    setChannelName('');
    setChannelLanguage('');
    setChannelNiche('');
    setTargetAudience('');
    setDefaultVoiceProfileId('');
    setDefaultAiModel('');
    setDefaultImageModel('');
    setPostingFrequency('');
    setPostingTimesText('');
    setMonthlyBudgetUsd('');
    setGrowthGoal('');
    setOperationalStatus('ACTIVE');
    setClientId('');
    setClientSecret('');
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ch, up, vd, vp, ai, img] = await Promise.all([
        listYoutubeChannels(),
        listYoutubeUploads(100),
        listVideos(200),
        listVoiceProfiles(),
        getAiSettings().catch(() => null),
        getImageSettings().catch(() => null)
      ]);
      setChannels(Array.isArray(ch?.data) ? ch.data : []);
      setUploads(Array.isArray(up?.data) ? up.data : []);
      setVideos(Array.isArray(vd?.data) ? vd.data : []);
      setVoiceProfiles(Array.isArray(vp?.data) ? vp.data : []);
      setAiSettings(ai?.data || null);
      setImageSettings(img?.data || null);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar YouTube');
    } finally {
      setLoading(false);
    }
  };

  const onImportCredentialsJson = async (file) => {
    try {
      const f = file || null;
      if (!f) return;
      if (!String(f.name || '').toLowerCase().endsWith('.json')) {
        toast.error('Selecione um arquivo .json');
        return;
      }
      setLoading(true);
      const text = await f.text();
      const parsed = JSON.parse(String(text || ''));
      const extracted = extractGoogleOAuthClient(parsed);
      if (!extracted) {
        toast.error('JSON inválido: não encontrei client_id e client_secret');
        return;
      }
      setClientId(extracted.clientId);
      setClientSecret(extracted.clientSecret);
      if (!String(channelName || '').trim()) {
        const fallbackName = extracted.projectId || extracted.clientId;
        setChannelName(fallbackName ? String(fallbackName).slice(0, 80) : '');
      }
      toast.success('Credenciais importadas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao importar JSON');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadMediaBank = async (youtubeChannel) => {
    const ch = youtubeChannel || null;
    const channelId = ch?.id != null ? Number(ch.id) : null;
    if (!channelId) return;
    setMediaBankLoading(true);
    try {
      const [img, vid] = await Promise.all([
        listMediaAssets('image', 200, { youtubeChannelId: channelId, includeShared: true }),
        listMediaAssets('video', 200, { youtubeChannelId: channelId, includeShared: true })
      ]);
      const images = Array.isArray(img?.data) ? img.data : [];
      const videos = Array.isArray(vid?.data) ? vid.data : [];
      setMediaBankImages(images);
      setMediaBankVideos(videos);

      const ids = [...new Set([...images.map((a) => Number(a.id)), ...videos.map((a) => Number(a.id))].filter(Boolean))];
      const previews = await Promise.all(ids.slice(0, 40).map(async (assetId) => {
        try {
          const resp = await mintPublicAssetLink(assetId, 240);
          return [assetId, resp?.data?.url || null];
        } catch {
          return [assetId, null];
        }
      }));
      setMediaBankPreviewUrls((prev) => {
        const next = { ...(prev || {}) };
        for (const [assetId, url] of previews) {
          if (url) next[String(assetId)] = url;
        }
        return next;
      });
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar mídia do canal');
    } finally {
      setMediaBankLoading(false);
    }
  };

  const openMediaForChannel = async (row) => {
    const ch = row || null;
    if (!ch?.id) return;
    setMediaBankChannel(ch);
    setOpenMediaBank(true);
    await loadMediaBank(ch);
  };

  const onUploadToMediaBank = async (kind, event) => {
    const file = event?.target?.files?.[0] || null;
    if (!file) return;
    const channelId = mediaBankChannel?.id != null ? Number(mediaBankChannel.id) : null;
    if (!channelId) return;
    setMediaBankUploadLoading(true);
    try {
      await uploadMediaAsset({ file, kind, youtubeChannelId: channelId });
      toast.success(`${kind === 'video' ? 'Vídeo' : 'Imagem'} enviado`);
      await loadMediaBank(mediaBankChannel);
    } catch (e) {
      toast.error(e?.message || 'Falha ao enviar mídia');
    } finally {
      setMediaBankUploadLoading(false);
      if (event?.target) event.target.value = '';
    }
  };

  const saveChannel = async () => {
    const postingTimesJson = parsePostingTimesText(postingTimesText);
    const monthlyBudgetValue = Number(monthlyBudgetUsd);
    if (!channelName.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!channelLanguage.trim()) {
      toast.error('Idioma é obrigatório');
      return;
    }
    if (!channelNiche.trim()) {
      toast.error('Nicho é obrigatório');
      return;
    }
    if (!targetAudience.trim()) {
      toast.error('Público alvo é obrigatório');
      return;
    }
    if (!defaultVoiceProfileId) {
      toast.error('Voz padrão é obrigatória');
      return;
    }
    if (!defaultAiModel.trim()) {
      toast.error('Modelo IA padrão é obrigatório');
      return;
    }
    if (!defaultImageModel.trim()) {
      toast.error('Modelo imagem padrão é obrigatório');
      return;
    }
    if (!postingFrequency.trim()) {
      toast.error('Frequência de postagem é obrigatória');
      return;
    }
    if (!postingTimesJson || !postingTimesJson.length) {
      toast.error('Horários de postagem inválidos. Use HH:MM separados por vírgula ou quebra de linha');
      return;
    }
    if (!Number.isFinite(monthlyBudgetValue) || monthlyBudgetValue < 0) {
      toast.error('Orçamento mensal inválido');
      return;
    }
    if (!growthGoal.trim()) {
      toast.error('Meta de crescimento é obrigatória');
      return;
    }
    if (!clientId.trim()) {
      toast.error('clientId é obrigatório');
      return;
    }
    if (!editingChannelId && !clientSecret.trim()) {
      toast.error('clientSecret é obrigatório');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: channelName.trim(),
        clientId: clientId.trim(),
        language: channelLanguage.trim(),
        niche: channelNiche.trim(),
        targetAudience: targetAudience.trim(),
        defaultVoiceProfileId: Number(defaultVoiceProfileId),
        defaultAiModel: defaultAiModel.trim(),
        defaultImageModel: defaultImageModel.trim(),
        postingFrequency: postingFrequency.trim(),
        postingTimesJson,
        monthlyBudgetUsd: monthlyBudgetValue,
        growthGoal: growthGoal.trim(),
        operationalStatus
      };
      if (editingChannelId) {
        if (clientSecret.trim()) payload.clientSecret = clientSecret.trim();
        await updateYoutubeChannel(editingChannelId, payload);
        toast.success('Canal atualizado');
      } else {
        await createYoutubeChannel({ ...payload, clientSecret: clientSecret.trim() });
        toast.success('Canal criado');
      }
      resetChannelForm();
      await loadAll();
    } catch (e) {
      toast.error(e?.message || (editingChannelId ? 'Falha ao atualizar canal' : 'Falha ao criar canal'));
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row) => {
    setEditingChannelId(row?.id || null);
    setChannelName(row?.name || '');
    setChannelLanguage(row?.language || '');
    setChannelNiche(row?.niche || '');
    setTargetAudience(row?.targetAudience || '');
    setDefaultVoiceProfileId(row?.defaultVoiceProfileId != null ? String(row.defaultVoiceProfileId) : '');
    setDefaultAiModel(row?.defaultAiModel || '');
    setDefaultImageModel(row?.defaultImageModel || '');
    setPostingFrequency(row?.postingFrequency || '');
    setPostingTimesText(Array.isArray(row?.postingTimesJson) ? row.postingTimesJson.join(', ') : '');
    setMonthlyBudgetUsd(row?.monthlyBudgetUsd != null ? String(row.monthlyBudgetUsd) : '');
    setGrowthGoal(row?.growthGoal || '');
    setOperationalStatus(row?.operationalStatus || 'ACTIVE');
    setClientId(row?.clientId || '');
    setClientSecret('');
    setOpenChannel(true);
  };

  const openDelete = (row) => {
    setDeletingChannel(row || null);
    setOpenDeleteChannel(true);
  };

  const confirmDelete = async () => {
    const id = deletingChannel?.id ? Number(deletingChannel.id) : 0;
    if (!id) return;
    setLoading(true);
    try {
      await deleteYoutubeChannel(id);
      toast.success('Canal excluído');
      setOpenDeleteChannel(false);
      setDeletingChannel(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Falha ao excluir canal');
    } finally {
      setLoading(false);
    }
  };

  const oauth = async (row) => {
    setLoading(true);
    try {
      const redirectUri = `${window.location.origin}/youtube/oauth-callback`;
      const resp = await startYoutubeOauth(row.id, redirectUri);
      const url = resp?.data?.url;
      if (!url) throw new Error('URL OAuth não retornada');
      window.location.href = url;
    } catch (e) {
      toast.error(e?.message || 'Falha ao iniciar OAuth');
      setLoading(false);
    }
  };

  const saveUpload = async () => {
    const youtubeChannelId = Number(selectedChannelId);
    const videoId = Number(selectedVideoId);
    if (!youtubeChannelId || !videoId) {
      toast.error('Selecione canal e vídeo');
      return;
    }
    setLoading(true);
    try {
      await createYoutubeUpload({
        youtubeChannelId,
        videoId,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        privacyStatus: privacyStatus || 'private',
        categoryId: categoryId || '27',
        playlistId: playlistId || null,
        isShorts: isShorts === true,
        seoAuto: seoAuto === true,
        tags: tagsText ? tagsText.split(',').map(s => s.trim()).filter(Boolean) : null,
        hashtags: hashtagsText ? hashtagsText.split(',').map(s => s.trim().replace(/^#/, '')).filter(Boolean) : null,
        title: titleOverride || null,
        description: descriptionOverride !== '' ? descriptionOverride : null,
        madeForKids: madeForKids === true
      });
      toast.success('Upload criado');
      setOpenUpload(false);
      setSelectedChannelId('');
      setSelectedVideoId('');
      setScheduledAt('');
      setPrivacyStatus('private');
      setCategoryId('27');
      setPlaylistId('');
      setIsShorts(false);
      setSeoAuto(true);
      setTagsText('');
      setHashtagsText('');
      setTitleOverride('');
      setDescriptionOverride('');
      setMadeForKids(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Falha ao criar upload');
    } finally {
      setLoading(false);
    }
  };

  const enqueue = async (row) => {
    setLoading(true);
    try {
      await enqueueYoutubeUpload(row.id);
      toast.success('Enfileirado');
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar upload');
    } finally {
      setLoading(false);
    }
  };

  const retry = async (row) => {
    setLoading(true);
    try {
      await retryYoutubeUpload(row.id);
      toast.success('Retry enfileirado');
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Falha ao reenfileirar');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: '27', label: 'Educação (27)' },
    { id: '22', label: 'Pessoas e blogs (22)' },
    { id: '25', label: 'Notícias e política (25)' },
    { id: '24', label: 'Entretenimento (24)' },
    { id: '28', label: 'Ciência e tecnologia (28)' },
    { id: '20', label: 'Jogos (20)' }
  ];

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">YouTube</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={loadAll} disabled={loading}>Atualizar</Button>
          <Button variant="outlined" onClick={() => navigate('/youtube-analytics')} disabled={loading}>Analytics</Button>
          <Button variant="contained" onClick={() => { resetChannelForm(); setOpenChannel(true); }} disabled={loading}>Novo canal</Button>
          <Button variant="contained" onClick={() => setOpenUpload(true)} disabled={loading}>Novo upload</Button>
        </Box>
      </Box>

      <Typography variant="h6">Canais</Typography>
      <DataTable
        rows={channels}
        loading={loading}
        columns={[
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'name', headerName: 'Nome', flex: 1, minWidth: 220 },
          { field: 'operationalStatus', headerName: 'Status Canal', width: 140 },
          { field: 'status', headerName: 'OAuth', width: 130 },
          { field: 'niche', headerName: 'Nicho', width: 180 },
          { field: 'language', headerName: 'Idioma', width: 120 },
          { field: 'defaultVoiceProfileName', headerName: 'Voz padrão', width: 180 },
          { field: 'defaultAiModel', headerName: 'IA padrão', width: 160 },
          { field: 'defaultImageModel', headerName: 'Imagem padrão', width: 160 },
          { field: 'monthlyBudgetUsd', headerName: 'Budget mensal', width: 140 },
          { field: 'channelId', headerName: 'Channel ID', width: 220 },
          {
            field: 'actions',
            headerName: 'Ações',
            width: 280,
            sortable: false,
            renderCell: (params) => (
              <Box display="flex" gap={1}>
                <Button size="small" variant="outlined" onClick={() => oauth(params.row)} disabled={loading}>
                  Conectar
                </Button>
                <Button size="small" variant="outlined" onClick={() => openMediaForChannel(params.row)} disabled={loading}>
                  Mídia
                </Button>
                <Button size="small" variant="outlined" onClick={() => openEdit(params.row)} disabled={loading}>
                  Editar
                </Button>
                <Button size="small" color="error" variant="outlined" onClick={() => openDelete(params.row)} disabled={loading}>
                  Excluir
                </Button>
              </Box>
            )
          }
        ]}
        getRowId={(r) => r.id}
      />

      <Typography variant="h6">Uploads</Typography>
      <DataTable
        rows={uploads}
        loading={loading}
        columns={[
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'videoId', headerName: 'Vídeo', width: 120 },
          { field: 'youtubeChannelId', headerName: 'Canal', width: 120 },
          { field: 'status', headerName: 'Status', width: 140 },
          { field: 'scheduledAt', headerName: 'Agendado', width: 180, valueGetter: (p) => (p?.row?.scheduledAt ? new Date(p.row.scheduledAt).toLocaleString() : '') },
          { field: 'youtubeVideoId', headerName: 'YouTube ID', width: 200 },
          {
            field: 'actions',
            headerName: 'Ações',
            width: 260,
            sortable: false,
            renderCell: (params) => (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={() => enqueue(params.row)} disabled={loading || ['uploaded', 'uploading', 'scheduled'].includes(String(params.row.status))}>
                  Enfileirar
                </Button>
                <Button size="small" variant="outlined" onClick={() => retry(params.row)} disabled={loading}>
                  Retry
                </Button>
                {params.row.youtubeVideoId ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${params.row.youtubeVideoId}`, '_blank', 'noopener,noreferrer')}
                    disabled={loading}
                  >
                    Abrir
                  </Button>
                ) : null}
              </Box>
            )
          }
        ]}
        getRowId={(r) => r.id}
      />

      <Dialog open={openChannel} onClose={resetChannelForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingChannelId ? 'Editar canal' : 'Novo canal'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField label="Nome" value={channelName} onChange={(e) => setChannelName(e.target.value)} fullWidth />
            <TextField label="Nicho" value={channelNiche} onChange={(e) => setChannelNiche(e.target.value)} fullWidth />
            <TextField label="Idioma" value={channelLanguage} onChange={(e) => setChannelLanguage(e.target.value)} fullWidth />
            <TextField label="Público alvo" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Voz padrão</InputLabel>
              <Select label="Voz padrão" value={defaultVoiceProfileId} onChange={(e) => setDefaultVoiceProfileId(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {voiceProfiles.map((vp) => (
                  <MenuItem key={vp.id} value={String(vp.id)}>{`${vp.name}${vp.provider ? ` (${vp.provider})` : ''}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Modelo IA padrão</InputLabel>
              <Select label="Modelo IA padrão" value={defaultAiModel} onChange={(e) => setDefaultAiModel(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {aiModelOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Modelo imagem padrão</InputLabel>
              <Select label="Modelo imagem padrão" value={defaultImageModel} onChange={(e) => setDefaultImageModel(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {imageModelOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Frequência de postagem" value={postingFrequency} onChange={(e) => setPostingFrequency(e.target.value)} fullWidth />
            <TextField
              label="Horários de postagem"
              value={postingTimesText}
              onChange={(e) => setPostingTimesText(e.target.value)}
              helperText="Use HH:MM separados por vírgula ou quebra de linha"
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Orçamento mensal (USD)"
              type="number"
              value={monthlyBudgetUsd}
              onChange={(e) => setMonthlyBudgetUsd(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField label="Meta de crescimento" value={growthGoal} onChange={(e) => setGrowthGoal(e.target.value)} fullWidth multiline minRows={2} />
            <FormControl fullWidth>
              <InputLabel>Status do canal</InputLabel>
              <Select label="Status do canal" value={operationalStatus} onChange={(e) => setOperationalStatus(e.target.value)}>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="PAUSED">PAUSED</MenuItem>
                <MenuItem value="ERROR">ERROR</MenuItem>
                <MenuItem value="ARCHIVED">ARCHIVED</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" component="label" disabled={loading}>
              Importar credenciais (JSON)
              <input
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const file = e?.target?.files?.[0] || null;
                  onImportCredentialsJson(file);
                  try { e.target.value = ''; } catch {}
                }}
              />
            </Button>
            <TextField label="Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} fullWidth />
            <TextField label="Client Secret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} fullWidth />
            {editingChannelId ? (
              <Typography variant="body2" color="text.secondary">
                Ao editar, se você deixar o Client Secret vazio, ele continuará o mesmo. Se você trocar Client ID/Secret, o canal volta para “disconnected” e precisa reconectar.
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetChannelForm} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={saveChannel} disabled={loading}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMediaBank} onClose={() => setOpenMediaBank(false)} maxWidth="md" fullWidth>
        <DialogTitle>Banco de mídia · {mediaBankChannel?.name || `Canal ${mediaBankChannel?.id || ''}`}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <Button variant="outlined" component="label" disabled={mediaBankLoading || mediaBankUploadLoading}>
                Upload imagem
                <input hidden type="file" accept="image/*" onChange={(e) => onUploadToMediaBank('image', e)} />
              </Button>
              <Button variant="outlined" component="label" disabled={mediaBankLoading || mediaBankUploadLoading}>
                Upload vídeo
                <input hidden type="file" accept="video/*" onChange={(e) => onUploadToMediaBank('video', e)} />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {mediaBankLoading ? 'Carregando...' : `${mediaBankImages.length} imagens, ${mediaBankVideos.length} vídeos`}
              </Typography>
            </Box>
            <Typography variant="subtitle1">Imagens</Typography>
            <DataTable
              rows={mediaBankImages}
              loading={mediaBankLoading}
              columns={[
                { field: 'id', headerName: 'ID', width: 90 },
                { field: 'fileName', headerName: 'Arquivo', flex: 1, minWidth: 240 },
                { field: 'createdAt', headerName: 'Criado', width: 180, valueGetter: (p) => (p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '') },
                {
                  field: 'preview',
                  headerName: 'Prévia',
                  width: 120,
                  sortable: false,
                  renderCell: (p) => {
                    const id = p?.row?.id != null ? String(p.row.id) : '';
                    const url = id ? mediaBankPreviewUrls[id] : null;
                    if (!url) return '-';
                    return <a href={url} target="_blank" rel="noreferrer">Abrir</a>;
                  }
                }
              ]}
              getRowId={(r) => r.id}
            />
            <Typography variant="subtitle1">Vídeos</Typography>
            <DataTable
              rows={mediaBankVideos}
              loading={mediaBankLoading}
              columns={[
                { field: 'id', headerName: 'ID', width: 90 },
                { field: 'fileName', headerName: 'Arquivo', flex: 1, minWidth: 240 },
                { field: 'createdAt', headerName: 'Criado', width: 180, valueGetter: (p) => (p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '') },
                {
                  field: 'preview',
                  headerName: 'Prévia',
                  width: 120,
                  sortable: false,
                  renderCell: (p) => {
                    const id = p?.row?.id != null ? String(p.row.id) : '';
                    const url = id ? mediaBankPreviewUrls[id] : null;
                    if (!url) return '-';
                    return <a href={url} target="_blank" rel="noreferrer">Abrir</a>;
                  }
                }
              ]}
              getRowId={(r) => r.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMediaBank(false)} disabled={mediaBankLoading || mediaBankUploadLoading}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteChannel} onClose={() => setOpenDeleteChannel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir canal</DialogTitle>
        <DialogContent>
          <Typography>
            Excluir o canal remove também configurações e históricos ligados a ele (uploads, automações, jobs). Produções vinculadas terão o canal removido.
          </Typography>
          <Typography mt={2}>
            Confirmar exclusão de: <strong>{deletingChannel?.name || `Canal ${deletingChannel?.id || ''}`}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteChannel(false)} disabled={loading}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={loading}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUpload} onClose={() => setOpenUpload(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo upload</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select label="Canal" value={selectedChannelId} onChange={(e) => setSelectedChannelId(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {channels.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name || `Canal ${c.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Vídeo (status ready)</InputLabel>
              <Select label="Vídeo (status ready)" value={selectedVideoId} onChange={(e) => setSelectedVideoId(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {readyVideos.map((v) => (
                  <MenuItem key={v.id} value={String(v.id)}>{`#${v.id} - ${v.title}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Agendar (opcional)"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Privacidade</InputLabel>
              <Select label="Privacidade" value={privacyStatus} onChange={(e) => setPrivacyStatus(e.target.value)}>
                <MenuItem value="private">private</MenuItem>
                <MenuItem value="unlisted">unlisted</MenuItem>
                <MenuItem value="public">public</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select label="Categoria" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Playlist ID (opcional)" value={playlistId} onChange={(e) => setPlaylistId(e.target.value)} fullWidth />
            <TextField label="Title override (opcional)" value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} fullWidth />
            <TextField label="Description override (opcional)" value={descriptionOverride} onChange={(e) => setDescriptionOverride(e.target.value)} fullWidth multiline minRows={3} />
            <TextField label="Tags (opcional, separado por vírgula)" value={tagsText} onChange={(e) => setTagsText(e.target.value)} fullWidth />
            <TextField label="Hashtags (opcional, separado por vírgula)" value={hashtagsText} onChange={(e) => setHashtagsText(e.target.value)} fullWidth />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={seoAuto === true} onChange={(e) => setSeoAuto(e.target.checked)} />
                <Typography>SEO automático</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={isShorts === true} onChange={(e) => setIsShorts(e.target.checked)} />
                <Typography>Shorts</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={madeForKids === true} onChange={(e) => setMadeForKids(e.target.checked)} />
                <Typography>Made for kids</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenUpload(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={saveUpload} disabled={loading}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
